import { isUuid } from '@mkp/shared/models';

const SEGMENT_SEPARATOR = ' | ';
const QUERY_PARAM_SEPARATOR = ' : ';
const UNNECESSARY_SEGMENTS = new Set(['workspace']);

// Routes that end in a uuid but are detail pages, not edit pages. The trailing-uuid heuristic
// below would otherwise tag /vacancy/:id as `| edit` and collide with the real edit page.
// Keyed on the filtered segment path.
const DETAIL_SEGMENTS_ENDING_IN_UUID = new Set(['vacancy']);

const SEGMENT_ALLOWED_KEYS = new Map<string, string[]>([
  ['vacancies', ['show']],
  ['vacancy | applications', ['id', 'status', 'tab']],
  ['vacancy | publish | product-options | jobs', ['step']],
  ['vacancy | publish | product-options | jobup', ['step']],
  ['vacancy | publish | product-options | job-scout-24', ['step']],
  ['vacancy | credits | product-options | jobs', ['step']],
  ['vacancy | credits | product-options | jobup', ['step']],
  ['vacancy | credits | product-options | job-scout-24', ['step']],
  ['credit-management | purchase-credits | order-summary', ['productCode']],
  ['credit-management | purchase-credits | comparison', ['platformGroup']],
  ['company | vacancies', ['status']],
  ['verify-identity', ['status']],
  ['user-management', ['tab']],
]);

const CATEGORY_OVERRIDES = new Map<string, string>([
  ['user-management', 'user-management | user | list'],
  ['user-management : invite', 'user-management | invite | list'],
]);

export const urlToPageCategory = (url: string): string => {
  if (!url) return '';

  const [path, query] = url.split('?');

  const rawSegments = path.split('/').filter(Boolean);
  const endsWithUuid = isUuid(rawSegments[rawSegments.length - 1] ?? '');

  // remove UUID and "workspace" so they don't show in page_category
  const filteredSegments = rawSegments.filter((s) => !isUuid(s) && !UNNECESSARY_SEGMENTS.has(s));

  const endsWithEditOrPreviewAfterFilter = ['edit', 'preview'].includes(
    filteredSegments[filteredSegments.length - 1]
  );

  // when a url ends with a UUID it's usually an edit page (sometimes a preview page)
  // -> we add the `| edit` manually to make `page_category` easier to understand
  // vacancy URLs are "edit/UUID" and "preview/UUID",
  // -> after filtering they already end with "edit" or "preview"; so we ignore those
  const needsEdit =
    endsWithUuid &&
    !endsWithEditOrPreviewAfterFilter &&
    !DETAIL_SEGMENTS_ENDING_IN_UUID.has(filteredSegments.join(SEGMENT_SEPARATOR));
  const segmentsArray = needsEdit ? [...filteredSegments, 'edit'] : filteredSegments;
  const segments = segmentsArray.join(SEGMENT_SEPARATOR);

  const allowedKeys = SEGMENT_ALLOWED_KEYS.get(segments) ?? [];

  const rawQueryParams = (query ?? '')
    .split('&')
    .map((param) => param.split('='))
    .filter(([key]) => allowedKeys.includes(key))
    .reduce(
      (acc, [key, value]) => ({ ...acc, ...(value ? { [key]: value } : {}) }),
      {} as Record<string, string>
    );

  const queryParams = isAts(segments) ? mapForAts(rawQueryParams) : Object.values(rawQueryParams);

  const result = [segments, queryParams.join(QUERY_PARAM_SEPARATOR)]
    .filter(Boolean)
    .join(QUERY_PARAM_SEPARATOR);

  // override values at the end to make them more readable by the tracking team
  // try to avoid overrides if possible
  return CATEGORY_OVERRIDES.get(result) ?? result;
};

const isAts = (segments: string) => segments === 'vacancy | applications';

const mapForAts = (queryParams: Record<string, string>): string[] =>
  [queryParams['status'], queryParams['tab'] ?? (queryParams['id'] ? 'details' : 'none')].filter(
    Boolean
  );
